import { useI18n } from "../../../i18n";
import { BEATS_GOAL, COURSE_LEN, OBSTACLES, TRACK_GOAL, moviePlaying } from "../lib/scenes";
import type { FaceCourse, Heartbeat, Movie, Track } from "../lib/scenes";

/**
 * 차시별 놀이 장면의 판(PICNIC-MB-FIELD-001 v0.2 §4 · 1단계 묶음) — 상태는 lib/scenes(순수), 여기는 그리기만.
 * 목표 줄은 `data-done` 으로 시험이 읽는다.
 */

export function Goal({ done, text, id }: { done: boolean; text: string; id: string }) {
  return (
    <p className="pf-mb__scene-goal" data-done={done} data-testid={id}>
      {done ? "✅" : "⬜"} {text}
    </p>
  );
}

/** 표정 리모컨 코스(L06) — 웃음 = 앞으로 · 슬픔 = 멈춤 · 놀람 = 점프 */
export function FaceCoursePanel({ s }: { s: FaceCourse }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-faceCourse">
      <div className="pf-mb__lane" style={{ gridTemplateColumns: `repeat(${COURSE_LEN + 1}, 1fr)` }} aria-label={t("student.microbit.scene.faceCourse.name")}>
        {Array.from({ length: COURSE_LEN + 1 }, (_, i) => (
          <span key={i} className="pf-mb__lane-cell" data-me={s.pos === i || undefined}>
            {s.pos === i ? (s.air > 0 ? "🤸" : s.done ? "🎉" : "🧒") : OBSTACLES.includes(i) ? "🪨" : i === COURSE_LEN ? "🏁" : ""}
          </span>
        ))}
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-course-mode">
        {t(`student.microbit.scene.faceCourse.mode.${s.air > 0 ? "jump" : s.mode}`)} · {s.pos}/{COURSE_LEN}
      </p>
      <Goal id="fs-mb-scene-goal" done={s.done} text={t("student.microbit.scene.faceCourse.goal")} />
    </div>
  );
}

/** 운동장 트랙(L13) — 흔들 때마다 한 걸음 */
export function TrackPanel({ s }: { s: Track }) {
  const { t } = useI18n();
  // 별 둘 · 셋(3D 무대)을 위해 목표 뒤에도 센다 — 이 판은 목표까지만 보인다
  const shown = Math.min(s.steps, TRACK_GOAL);
  const pct = Math.round((shown / TRACK_GOAL) * 100);
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-track">
      <div className="pf-mb__bar" role="progressbar" aria-valuemin={0} aria-valuemax={TRACK_GOAL} aria-valuenow={shown}>
        <span className="pf-mb__bar-fill" style={{ width: `${pct}%` }} />
        <span className="pf-mb__bar-runner" style={{ left: `${pct}%` }}>{s.steps >= TRACK_GOAL ? "🎉" : "🏃"}</span>
      </div>
      <p className="pf-mb__scene-state">{t("student.microbit.scene.track.steps", { n: shown, goal: TRACK_GOAL })}</p>
      <Goal id="fs-mb-scene-goal" done={s.steps >= TRACK_GOAL} text={t("student.microbit.scene.track.goal", { goal: TRACK_GOAL })} />
    </div>
  );
}

/** 두근두근 달리기(L05) — 밝음 ↔ 어두움이 한 번 두근 */
export function HeartbeatPanel({ s, level }: { s: Heartbeat; level: number }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-heartbeat">
      <div className="pf-mb__heart" aria-hidden="true" style={{ transform: `scale(${0.7 + (level / 9) * 0.6})` }}>❤️</div>
      <p className="pf-mb__scene-state">
        {t("student.microbit.scene.heartbeat.beats", { n: s.beats })} · 🏃 {Math.floor(s.run)}
      </p>
      <Goal id="fs-mb-scene-goal" done={s.beats >= BEATS_GOAL} text={t("student.microbit.scene.heartbeat.goal", { goal: BEATS_GOAL })} />
    </div>
  );
}

/** 미니 영화관(L03) — 서로 다른 그림이 차례로 돌면 상영 */
export function MoviePanel({ s, leds }: { s: Movie; leds: readonly number[] | null }) {
  const { t } = useI18n();
  const on = moviePlaying(s);
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-movie">
      <div className="pf-mb__screen" data-playing={on || undefined}>
        {(leds ?? Array(25).fill(0)).map((v, i) => (
          <span key={i} style={{ opacity: v === 0 ? 0.08 : 0.25 + (0.75 * v) / 9 }} />
        ))}
      </div>
      <p className="pf-mb__scene-state">{on ? t("student.microbit.scene.movie.playing", { n: s.loop }) : t("student.microbit.scene.movie.waiting")}</p>
      <Goal id="fs-mb-scene-goal" done={on} text={t("student.microbit.scene.movie.goal")} />
    </div>
  );
}
